/**
 * The MapLibre style, built at runtime rather than shipped as a static JSON.
 *
 * Why build it: the tile URL comes from the server's basemap config, so the
 * client has no provider baked in. If the source chain changes on the server,
 * the next page load picks it up without a rebuild.
 *
 * This is a deliberately plain style. Phase 0 is about proving the data chain,
 * not about cartography — the map exists so there is something to click.
 */

import type { StyleSpecification } from "maplibre-gl";

/** Muted operational-map palette: the terrain reads as background, not subject.
 *
 * ── 🔴 LAND AND SEA WERE THE SAME DARKNESS ──────────────────────────────────
 * Owner, 2026-09-17: *"Det är mörkgrått land mot mörkblått hav och det är svårt
 * att se skillnad."*
 *
 * He is right and it is measurable rather than a matter of taste. The two were
 * `#151b21` and `#12212f`: almost the same lightness, differing mainly in the
 * blue channel (0x21 against 0x2f). On anything but a calibrated screen in a
 * dark room, a coastline vanished — which on an island campaign is the one line
 * that has to read.
 *
 * Separated on **two** axes rather than one, because either alone is fragile:
 * lightness (land lifted, water deepened) so the edge survives a bright room,
 * and hue (land warmer, water bluer) so it survives colour-blind vision where
 * lightness is doing all the work. The terrain still reads as background — this
 * is a wider gap between two quiet colours, not a brighter map.
 */
const PALETTE = {
  water: "#0b1a27",
  land: "#1e2630",
  landuseGreen: "#17231c",
  built: "#1c232b",
  roadMinor: "#2c3742",
  roadMajor: "#3d4a58",
  rail: "#37414d",
  boundary: "#4a3b46",
  label: "#8fa0b3",
  labelHalo: "#0c1116",
};

/**
 * Road colours, and why there are two sets.
 *
 * Owner, 2026-09-17: *"Går det att ändra färg vägarna som visas över flygfoto?
 * De syns dåligt."*
 *
 * The layer order was already right — the photo is anchored beneath
 * `roads-minor`, so roads draw on top of it. The problem is the palette: these
 * are dark slate, chosen to sit quietly on a dark vector map, and dark slate on
 * a photograph of Sicilian farmland is nearly invisible.
 *
 * One colour cannot serve both grounds. A road bright enough to read over a
 * photo glares on the dark map, and the compromise is worse on both. So the
 * paint follows the imagery toggle, which the client already tracks.
 *
 * ⚠️ Cool grey rather than the obvious pale sand: `offroad-line` is amber
 * (`#d6a24a`), and a warm road would be hard to tell from a cross-country march
 * — the one distinction the map already makes between a road and not-a-road.
 * Cool grey collides with nothing: not amber, not the cyan movement badge, not
 * the violet resting badge, not the saturated ownership fills.
 */
export const ROADS_OVER_IMAGERY = { major: "#e6ecf2", minor: "#b9c6d2" };
export const ROADS_OVER_MAP = { major: "#3d4a58", minor: "#2c3742" };

export function buildStyle(tileUrl: string, attribution: string): StyleSpecification {
  return {
    version: 8,
    // MapLibre needs a glyph source for any text layer. This is the one
    // external asset the client loads directly; if it fails the map still
    // renders, only without labels.
    glyphs: "https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf",
    sources: {
      basemap: {
        type: "vector",
        // Built by concatenation, NOT `new URL()`.
        //
        // `new URL("/api/tile/{z}/{x}/{y}", origin)` percent-encodes the braces
        // into %7Bz%7D, so MapLibre never sees the placeholders, requests that
        // literal path, gets a 400, and renders an empty map with no error in
        // the console. Cost an hour on 2026-09-08; do not "tidy" this back.
        tiles: [window.location.origin + tileUrl],
        minzoom: 0,
        maxzoom: 14,
        attribution,
      },
    },
    layers: [
      {
        id: "background",
        type: "background",
        paint: { "background-color": PALETTE.land },
      },
      {
        id: "landuse",
        type: "fill",
        source: "basemap",
        "source-layer": "landcover",
        paint: { "fill-color": PALETTE.landuseGreen, "fill-opacity": 0.55 },
      },
      {
        id: "built-up",
        type: "fill",
        source: "basemap",
        "source-layer": "landuse",
        paint: { "fill-color": PALETTE.built, "fill-opacity": 0.5 },
      },
      {
        id: "water",
        type: "fill",
        source: "basemap",
        "source-layer": "water",
        paint: { "fill-color": PALETTE.water },
      },
      {
        id: "waterway",
        type: "line",
        source: "basemap",
        "source-layer": "waterway",
        paint: {
          "line-color": PALETTE.water,
          "line-width": ["interpolate", ["linear"], ["zoom"], 8, 0.5, 16, 3],
        },
      },
      {
        id: "roads-minor",
        type: "line",
        source: "basemap",
        "source-layer": "transportation",
        filter: ["!in", "class", "motorway", "trunk", "primary", "rail"],
        minzoom: 10,
        paint: {
          "line-color": PALETTE.roadMinor,
          "line-width": ["interpolate", ["linear"], ["zoom"], 10, 0.4, 18, 4],
        },
      },
      {
        id: "roads-major",
        type: "line",
        source: "basemap",
        "source-layer": "transportation",
        filter: ["in", "class", "motorway", "trunk", "primary"],
        paint: {
          "line-color": PALETTE.roadMajor,
          "line-width": ["interpolate", ["linear"], ["zoom"], 6, 0.6, 18, 7],
        },
      },
      {
        id: "rail",
        type: "line",
        source: "basemap",
        "source-layer": "transportation",
        filter: ["==", "class", "rail"],
        minzoom: 9,
        paint: {
          "line-color": PALETTE.rail,
          "line-width": ["interpolate", ["linear"], ["zoom"], 9, 0.4, 18, 2.5],
          "line-dasharray": [3, 2],
        },
      },
      {
        id: "boundary",
        type: "line",
        source: "basemap",
        "source-layer": "boundary",
        filter: ["<=", "admin_level", 2],
        paint: {
          "line-color": PALETTE.boundary,
          "line-width": ["interpolate", ["linear"], ["zoom"], 3, 0.5, 10, 1.6],
          "line-dasharray": [4, 2],
        },
      },
      {
        id: "place-labels",
        type: "symbol",
        source: "basemap",
        "source-layer": "place",
        layout: {
          "text-field": ["get", "name"],
          "text-font": ["Noto Sans Regular"],
          "text-size": ["interpolate", ["linear"], ["zoom"], 4, 10, 12, 14],
          "text-transform": "uppercase",
          "text-letter-spacing": 0.12,
        },
        paint: {
          "text-color": PALETTE.label,
          "text-halo-color": PALETTE.labelHalo,
          "text-halo-width": 1.4,
        },
      },
    ],
  };
}

/**
 * Fallback style for when the basemap chain is fully down.
 *
 * An empty dark field is not useful, but it is honest — and it keeps the map
 * object alive so the app does not have to handle a null map everywhere.
 */
export function buildBlankStyle(): StyleSpecification {
  return {
    version: 8,
    sources: {},
    layers: [
      { id: "background", type: "background", paint: { "background-color": PALETTE.land } },
    ],
  };
}
